/**
 * CR-7168: Column configuration for the Submitted Appeals table.
 *
 * Follows the deliveryColumnConfig pattern: per-column visibility/order config
 * persisted to localStorage per user, merged against DEFAULT so newly-added
 * columns appear automatically.
 *
 * View and Action columns are excluded from configuration - they are always
 * visible and pinned to the right edge (sticky) since they are essential for
 * responding to appeals.
 */

export interface AppealColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  minWidth: string;
  order: number;
  /** CSV header name for export filtering. Columns without csvHeader are not in export. */
  csvHeader?: string;
}

/**
 * Default column set for the Submitted Appeals table.
 * View and Action are NOT included - they are always rendered and pinned.
 */
export const DEFAULT_APPEAL_COLUMNS: AppealColumnConfig[] = [
  { id: 'appeal_id', label: 'Appeal #', visible: true, minWidth: '90px', order: 0, csvHeader: 'Appeal #' },
  { id: 'grn_number', label: 'GRN Number', visible: true, minWidth: '120px', order: 1, csvHeader: 'GRN Number' },
  { id: 'po_number', label: 'PO Number', visible: true, minWidth: '120px', order: 2, csvHeader: 'PO Number' },
  { id: 'line_number', label: 'Line #', visible: true, minWidth: '80px', order: 3, csvHeader: 'Line #' },
  { id: 'part_number', label: 'Part Number', visible: true, minWidth: '120px', order: 4, csvHeader: 'Part Number' },
  { id: 'supplier_name', label: 'Supplier', visible: true, minWidth: '150px', order: 5, csvHeader: 'Supplier' },
  { id: 'status', label: 'Status', visible: true, minWidth: '120px', order: 6, csvHeader: 'Status' },
  { id: 'on_time_status', label: 'On-Time', visible: true, minWidth: '100px', order: 7, csvHeader: 'On-Time Status' },
  { id: 'otif_overridden', label: 'OTIF Overridden', visible: false, minWidth: '130px', order: 8 }, // Not in CSV
  { id: 'grn_mrp_date', label: 'MRP Date', visible: false, minWidth: '110px', order: 9, csvHeader: 'MRP Date' },
  { id: 'grn_review_date', label: 'Review Date', visible: false, minWidth: '120px', order: 10, csvHeader: 'Review Date' },
  { id: 'grn_original_delivery_date', label: 'ODD', visible: true, minWidth: '110px', order: 11, csvHeader: 'ODD' },
  { id: 'grn_receipt_date', label: 'Delivery Date', visible: true, minWidth: '120px', order: 12, csvHeader: 'Delivery Date' },
  { id: 'supplier_note', label: 'Supplier Note', visible: true, minWidth: '200px', order: 13, csvHeader: 'Supplier Note' },
  { id: 'created_at', label: 'Created', visible: true, minWidth: '110px', order: 14, csvHeader: 'Created At' },
];

export const APPEAL_COLUMN_STORAGE_KEY = 'appeal_column_config';

/** Per-user storage key so a shared browser doesn't clobber layouts. */
function columnStorageKey(userId?: number | string): string {
  return userId != null
    ? `${APPEAL_COLUMN_STORAGE_KEY}:${userId}`
    : APPEAL_COLUMN_STORAGE_KEY;
}

/**
 * Load persisted appeal columns, merged against DEFAULT so that new columns
 * (added in later releases) always surface even for users with saved layouts.
 * Only visibility + order are taken from storage; all other metadata (label,
 * minWidth) is authoritative from DEFAULT.
 *
 * @param userId - Current user id, used to scope the storage key.
 * @returns Ordered column config array.
 */
export function loadAppealColumns(userId?: number | string): AppealColumnConfig[] {
  try {
    const stored = localStorage.getItem(columnStorageKey(userId));
    if (!stored) return DEFAULT_APPEAL_COLUMNS;
    const parsed = JSON.parse(stored) as AppealColumnConfig[];
    const storedMap = new Map(parsed.map((c) => [c.id, c]));
    return DEFAULT_APPEAL_COLUMNS.map((def) => {
      const saved = storedMap.get(def.id);
      return saved ? { ...def, visible: saved.visible, order: saved.order } : def;
    }).sort((a, b) => a.order - b.order);
  } catch {
    return DEFAULT_APPEAL_COLUMNS;
  }
}

/**
 * Persist the current appeal column layout for a user.
 *
 * @param cols - Column config to persist.
 * @param userId - Current user id, used to scope the storage key.
 */
export function saveAppealColumns(cols: AppealColumnConfig[], userId?: number | string): void {
  localStorage.setItem(columnStorageKey(userId), JSON.stringify(cols));
}

/**
 * Filter and reorder CSV content based on visible columns.
 * Columns are reordered to match the user's column order.
 * Hidden columns are removed from the export.
 *
 * @param csvContent - Raw CSV string from backend
 * @param columns - Current column configuration (with visibility and order)
 * @returns Filtered and reordered CSV string
 */
export function filterCsvByVisibleColumns(csvContent: string, columns: AppealColumnConfig[]): string {
  // Normalize line endings: convert \r\n to \n, then remove any stray \r
  const normalizedContent = csvContent.replace(/\r\n/g, '\n').replace(/\r/g, '');
  const lines = normalizedContent.trim().split('\n');
  if (lines.length === 0) return csvContent;

  // Parse header row to get column indices
  const headerRow = lines[0];
  const headers = parseCSVRow(headerRow);

  // Build mapping of CSV header -> original index
  const headerIndexMap = new Map<string, number>();
  headers.forEach((h, i) => headerIndexMap.set(h.trim(), i));

  // Get visible columns with csvHeader, sorted by user order
  const visibleWithCsv = columns
    .filter((c) => c.visible && c.csvHeader)
    .sort((a, b) => a.order - b.order);

  // Get indices of visible columns in original CSV order
  const visibleIndices: number[] = [];
  const newHeaders: string[] = [];
  for (const col of visibleWithCsv) {
    const idx = headerIndexMap.get(col.csvHeader!);
    if (idx !== undefined) {
      visibleIndices.push(idx);
      newHeaders.push(col.csvHeader!);
    }
  }

  // Also include columns not in our config (Customer, Created By, etc.) at the end
  const configuredHeaders = new Set(columns.map((c) => c.csvHeader).filter(Boolean));
  headers.forEach((h, i) => {
    if (!configuredHeaders.has(h.trim()) && !visibleIndices.includes(i)) {
      visibleIndices.push(i);
      newHeaders.push(h.trim());
    }
  });

  // Build new CSV with filtered/reordered columns
  const newLines: string[] = [newHeaders.join(',')];
  for (let i = 1; i < lines.length; i++) {
    const row = parseCSVRow(lines[i]);
    const newRow = visibleIndices.map((idx) => row[idx] ?? '');
    newLines.push(newRow.map((cell) => escapeCsvCell(cell)).join(','));
  }

  return newLines.join('\n');
}

/** Parse a CSV row handling quoted fields with commas. */
function parseCSVRow(row: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < row.length; i++) {
    const char = row[i];
    if (char === '"') {
      if (inQuotes && row[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);
  return result;
}

/** Escape a cell value for CSV output. */
function escapeCsvCell(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}
